import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlatformInvitationCreateRequest, PlatformOrgUpdateRequest } from '@hangar/shared';
import {
  cancelClientInvitation, deleteClient, inviteToClient, listClientInvitations, reactivateClient, resendClientInvitation, suspendClient, updateClientOrg,
} from './endpointsLifecycle';
import { membersKeys, useClientOrgs } from './hooksMembers';
import type { ClientOrg, Invitation } from './schemasMembers';

/* ---------- Client lifecycle (SPEC §17.6, platform operators only) ---------- */

export const lifecycleKeys = {
  invitations: (orgId: string) => ['platform-orgs', orgId, 'invitations'] as const,
};

/** One client, from the operator's client list (there is no single-client route). */
export function useClientOrg(orgId: string, enabled = true) {
  const list = useClientOrgs(enabled);
  return { ...list, org: list.data?.find((o) => o.id === orgId) };
}

/** Puts a client the API answered into the cached list, then refetches it. */
function usePutClient() {
  const qc = useQueryClient();
  return (org: ClientOrg | null, orgId: string) => {
    qc.setQueryData<ClientOrg[]>(membersKeys.clients, (old) => (old ? (org ? old.map((o) => (o.id === org.id ? org : o)) : old.filter((o) => o.id !== orgId)) : old));
    void qc.invalidateQueries({ queryKey: membersKeys.clients });
  };
}

export function useClientMutations(orgId: string) {
  const put = usePutClient();
  const update = useMutation({ mutationFn: (body: PlatformOrgUpdateRequest) => updateClientOrg(orgId, body), onSuccess: (o) => put(o, orgId) });
  const suspend = useMutation({ mutationFn: (reason: string) => suspendClient(orgId, reason), onSuccess: (o) => put(o, orgId) });
  const reactivate = useMutation({ mutationFn: () => reactivateClient(orgId), onSuccess: (o) => put(o, orgId) });
  const remove = useMutation({ mutationFn: (confirmSlug: string) => deleteClient(orgId, confirmSlug), onSuccess: (d) => put(d.org, orgId) });
  return { update, suspend, reactivate, remove };
}

export const useClientInvitations = (orgId: string, enabled = true) =>
  useQuery({ queryKey: lifecycleKeys.invitations(orgId), queryFn: () => listClientInvitations(orgId), enabled });

export function useClientInvitationMutations(orgId: string) {
  const qc = useQueryClient();
  const key = lifecycleKeys.invitations(orgId);
  const settle = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: membersKeys.clients }); // the list counts open invitations
  };
  const put = (i: Invitation) => qc.setQueryData<Invitation[]>(key, (old) => (old ? [i, ...old.filter((x) => x.id !== i.id)] : old));
  const invite = useMutation({
    mutationFn: (v: { body: PlatformInvitationCreateRequest; idempotencyKey: string }) => inviteToClient(orgId, v.body, v.idempotencyKey),
    onSuccess: (i) => { put(i); settle(); },
  });
  const resend = useMutation({ mutationFn: (id: string) => resendClientInvitation(orgId, id), onSuccess: (i) => { put(i); settle(); } });
  const revoke = useMutation({
    mutationFn: (id: string) => cancelClientInvitation(orgId, id),
    onSuccess: (_none, id) => { qc.setQueryData<Invitation[]>(key, (old) => old?.filter((x) => x.id !== id)); settle(); },
  });
  return { invite, resend, revoke };
}
